import React, { useMemo } from 'react';

const AnimatedLoginBackground = () => {
  // Generate stable random values for floating elements
  const floatingPercentages = useMemo(() => 
    Array.from({ length: 8 }, () => ({
      left: Math.random() * 100,
      top: Math.random() * 100,
      value: Math.floor(Math.random() * 10 + 5),
      duration: 12 + Math.random() * 8,
      delay: Math.random() * 3
    })), []
  );

  const floatingDollars = useMemo(() =>
    Array.from({ length: 12 }, () => ({
      left: Math.random() * 100,
      top: Math.random() * 100,
      rotation: Math.random() * 360,
      duration: 10 + Math.random() * 10,
      delay: Math.random() * 4
    })), []
  );

  const floatingParticles = useMemo(() =>
    Array.from({ length: 15 }, () => ({
      left: Math.random() * 100,
      top: Math.random() * 100,
      size: Math.random() * 6 + 2,
      duration: 15 + Math.random() * 10,
      delay: Math.random() * 5
    })), []
  );
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
      {/* Gradient overlay with smooth transitions */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50 dark:from-primary-900 dark:via-primary-800 dark:to-primary-900 opacity-95 transition-opacity duration-1000" />
      
      {/* Animated SVG elements container */}
      <div className="absolute inset-0">
        {/* Dollar Sign Animations */}
        <div className="absolute top-20 left-10 opacity-20 dark:opacity-10 animate-float-slow">
          <svg width="120" height="120" viewBox="0 0 100 100" className="animate-spin-slow">
            <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="2" className="text-primary-400 dark:text-primary-600" />
            <path d="M50 20 L50 80 M35 35 L65 35 M35 65 L65 65" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="text-primary-500 dark:text-primary-500" />
            <text x="50" y="60" textAnchor="middle" fontSize="40" fill="currentColor" className="text-primary-500 dark:text-primary-500 font-bold">$</text>
          </svg>
        </div>
        
        <div className="absolute top-40 right-20 opacity-15 dark:opacity-8 animate-float-medium">
          <svg width="100" height="100" viewBox="0 0 100 100" className="animate-pulse-slow">
            <circle cx="50" cy="50" r="35" fill="none" stroke="currentColor" strokeWidth="2" className="text-secondary-400 dark:text-secondary-600" />
            <text x="50" y="60" textAnchor="middle" fontSize="35" fill="currentColor" className="text-secondary-500 dark:text-secondary-500 font-bold">$</text>
          </svg>
        </div>
        
        <div className="absolute bottom-32 left-20 opacity-18 dark:opacity-9 animate-float-fast">
          <svg width="90" height="90" viewBox="0 0 100 100" className="animate-bounce-slow">
            <text x="50" y="60" textAnchor="middle" fontSize="30" fill="currentColor" className="text-accent-500 dark:text-accent-400 font-bold">$</text>
          </svg>
        </div>


        {/* Portfolio Pie Chart Animation */}
        <div className="absolute top-1/5 left-1/3 opacity-20 dark:opacity-12 animate-float-slow">
          <svg width="140" height="140" viewBox="0 0 140 140" className="animate-rotate-slow">
            <g className="text-primary-500 dark:text-primary-400">
              {/* Pie slices */}
              <path
                d="M70,70 L70,10 A60,60 0 0,1 120,70 Z"
                fill="currentColor"
                opacity="0.4"
                className="animate-pulse"
              />
              <path
                d="M70,70 L120,70 A60,60 0 0,1 100,120 Z"
                fill="currentColor"
                opacity="0.5"
                className="animate-pulse-delayed"
              />
              <path
                d="M70,70 L100,120 A60,60 0 0,1 40,120 Z"
                fill="currentColor"
                opacity="0.3"
                className="animate-pulse"
              />
              <path
                d="M70,70 L40,120 A60,60 0 0,1 20,70 Z"
                fill="currentColor"
                opacity="0.4"
                className="animate-pulse-delayed"
              />
              <path
                d="M70,70 L20,70 A60,60 0 0,1 70,10 Z"
                fill="currentColor"
                opacity="0.35"
                className="animate-pulse"
              />
              <circle cx="70" cy="70" r="35" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.6" />
            </g>
          </svg>
        </div>

        {/* ROI Percentage Indicator */}
        <div className="absolute bottom-1/3 right-1/5 opacity-18 dark:opacity-10 animate-float-medium">
          <svg width="110" height="110" viewBox="0 0 110 110" className="animate-grow">
            <g className="text-accent-500 dark:text-accent-400">
              {/* Circular progress */}
              <circle
                cx="55"
                cy="55"
                r="45"
                fill="none"
                stroke="currentColor"
                strokeWidth="4"
                opacity="0.2"
              />
              <circle
                cx="55"
                cy="55"
                r="45"
                fill="none"
                stroke="currentColor"
                strokeWidth="4"
                strokeDasharray="283"
                strokeDashoffset="85"
                strokeLinecap="round"
                className="animate-draw-line"
                style={{ transform: 'rotate(-90deg)', transformOrigin: '55px 55px' }}
              />
              <text
                x="55"
                y="50"
                textAnchor="middle"
                fontSize="24"
                fill="currentColor"
                fontWeight="bold"
                className="animate-pulse"
              >
                15%
              </text>
              <text
                x="55"
                y="70"
                textAnchor="middle"
                fontSize="12"
                fill="currentColor"
                opacity="0.7"
              >
                ROI
              </text>
            </g>
          </svg>
        </div>

        {/* Growth Arrow/Trending Up */}
        <div className="absolute top-2/3 right-1/6 opacity-22 dark:opacity-12 animate-slide-in-right">
          <svg width="130" height="130" viewBox="0 0 130 130" className="animate-bounce-slow">
            <g className="text-accent-500 dark:text-accent-400">
              {/* Upward trending arrow */}
              <path
                d="M20,100 L40,80 L50,70 L60,60 L70,50 L80,40 L90,30 L100,20"
                fill="none"
                stroke="currentColor"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="animate-draw-line"
              />
              <polygon
                points="100,20 110,20 105,10"
                fill="currentColor"
                className="animate-pulse"
              />
              {/* Growth indicators */}
              <circle cx="40" cy="80" r="3" fill="currentColor" className="animate-pulse" />
              <circle cx="60" cy="60" r="3" fill="currentColor" className="animate-pulse-delayed" />
              <circle cx="80" cy="40" r="3" fill="currentColor" className="animate-pulse" />
            </g>
          </svg>
        </div>

        {/* Investment Coin/Token */}
        <div className="absolute top-1/6 right-1/4 opacity-20 dark:opacity-11 animate-float-fast">
          <svg width="100" height="100" viewBox="0 0 100 100" className="animate-spin-slow">
            <g className="text-secondary-500 dark:text-secondary-400">
              <circle cx="50" cy="50" r="40" fill="currentColor" opacity="0.2" />
              <circle cx="50" cy="50" r="35" fill="none" stroke="currentColor" strokeWidth="2" />
              <circle cx="50" cy="50" r="25" fill="currentColor" opacity="0.3" />
              <text x="50" y="58" textAnchor="middle" fontSize="20" fill="currentColor" fontWeight="bold">
                $
              </text>
              <circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.5" />
            </g>
          </svg>
        </div>

        {/* Portfolio Value Display */}
        <div className="absolute bottom-1/5 left-1/4 opacity-16 dark:opacity-9 animate-slide-in-left">
          <svg width="160" height="80" viewBox="0 0 160 80">
            <g className="text-primary-500 dark:text-primary-400">
              {/* Card background */}
              <rect
                x="10"
                y="10"
                width="140"
                height="60"
                rx="8"
                fill="currentColor"
                opacity="0.1"
                className="animate-pulse-slow"
              />
              <rect
                x="10"
                y="10"
                width="140"
                height="60"
                rx="8"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                opacity="0.3"
              />
              {/* Value text */}
              <text
                x="80"
                y="35"
                textAnchor="middle"
                fontSize="18"
                fill="currentColor"
                fontWeight="bold"
                className="animate-pulse"
              >
                $125,000
              </text>
              <text
                x="80"
                y="55"
                textAnchor="middle"
                fontSize="12"
                fill="currentColor"
                opacity="0.7"
              >
                Portfolio Value
              </text>
            </g>
          </svg>
        </div>

        {/* Profit Indicator */}
        <div className="absolute top-3/4 left-1/5 opacity-19 dark:opacity-11 animate-float-slow">
          <svg width="120" height="120" viewBox="0 0 120 120">
            <g className="text-accent-500 dark:text-accent-400">
              {/* Plus sign for profit */}
              <circle cx="60" cy="60" r="45" fill="currentColor" opacity="0.15" className="animate-pulse-slow" />
              <path
                d="M60,35 L60,85 M35,60 L85,60"
                stroke="currentColor"
                strokeWidth="5"
                strokeLinecap="round"
                className="animate-pulse"
              />
              <text
                x="60"
                y="70"
                textAnchor="middle"
                fontSize="16"
                fill="currentColor"
                fontWeight="bold"
              >
                +
              </text>
            </g>
          </svg>
        </div>

      </div>

      {/* Investment-themed floating elements */}
      <div className="absolute inset-0">
        {/* Floating percentage indicators */}
        {floatingPercentages.map((item, i) => (
          <div
            key={`percent-${i}`}
            className="absolute opacity-15 dark:opacity-8"
            style={{
              left: item.left + '%',
              top: item.top + '%',
              animation: `float ${item.duration}s ease-in-out infinite`,
              animationDelay: item.delay + 's'
            }}
          >
            <svg width="60" height="30" viewBox="0 0 60 30" className="text-accent-500 dark:text-accent-400">
              <text
                x="30"
                y="20"
                textAnchor="middle"
                fontSize="14"
                fill="currentColor"
                fontWeight="bold"
                className="animate-pulse"
              >
                +{item.value}%
              </text>
            </svg>
          </div>
        ))}
        
        {/* Floating dollar signs */}
        {floatingDollars.map((item, i) => (
          <div
            key={`dollar-${i}`}
            className="absolute opacity-20 dark:opacity-10"
            style={{
              left: item.left + '%',
              top: item.top + '%',
              animation: `float ${item.duration}s ease-in-out infinite`,
              animationDelay: item.delay + 's',
              transform: `rotate(${item.rotation}deg)`
            }}
          >
            <svg width="40" height="40" viewBox="0 0 40 40">
              <text
                x="20"
                y="28"
                textAnchor="middle"
                fontSize="24"
                fill="currentColor"
                className="text-primary-400 dark:text-primary-500 animate-pulse-slow"
              >
                $
              </text>
            </svg>
          </div>
        ))}
        
        {/* Floating particles */}
        {floatingParticles.map((item, i) => (
          <div
            key={`particle-${i}`}
            className="absolute rounded-full bg-primary-200 dark:bg-primary-700 opacity-30 dark:opacity-20"
            style={{
              width: item.size + 'px',
              height: item.size + 'px',
              left: item.left + '%',
              top: item.top + '%',
              animation: `float ${item.duration}s ease-in-out infinite`,
              animationDelay: item.delay + 's'
            }}
          />
        ))}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
            opacity: 0.3;
          }
          25% {
            transform: translateY(-20px) translateX(10px);
            opacity: 0.5;
          }
          50% {
            transform: translateY(-40px) translateX(-10px);
            opacity: 0.4;
          }
          75% {
            transform: translateY(-20px) translateX(5px);
            opacity: 0.5;
          }
        }
        
        @keyframes float-slow {
          0%, 100% {
            transform: translateY(0) translateX(0) rotate(0deg);
          }
          33% {
            transform: translateY(-15px) translateX(8px) rotate(2deg);
          }
          66% {
            transform: translateY(-30px) translateX(-8px) rotate(-2deg);
          }
        }
        
        @keyframes float-medium {
          0%, 100% {
            transform: translateY(0) translateX(0);
          }
          50% {
            transform: translateY(-25px) translateX(12px);
          }
        }
        
        @keyframes float-fast {
          0%, 100% {
            transform: translateY(0) translateX(0);
          }
          50% {
            transform: translateY(-18px) translateX(-10px);
          }
        }
        
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        @keyframes pulse-slow {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.7;
            transform: scale(1.1);
          }
        }
        
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        @keyframes slide-in-right {
          0% {
            transform: translateX(100px);
            opacity: 0;
          }
          100% {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes slide-in-left {
          0% {
            transform: translateX(-100px);
            opacity: 0;
          }
          100% {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes grow {
          0% {
            transform: scale(0.8);
            opacity: 0;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
        
        @keyframes draw-line {
          0% {
            stroke-dasharray: 1000;
            stroke-dashoffset: 1000;
          }
          100% {
            stroke-dasharray: 1000;
            stroke-dashoffset: 0;
          }
        }
        
        @keyframes draw-line-delayed {
          0% {
            stroke-dasharray: 1000;
            stroke-dashoffset: 1000;
          }
          50% {
            stroke-dasharray: 1000;
            stroke-dashoffset: 1000;
          }
          100% {
            stroke-dasharray: 1000;
            stroke-dashoffset: 0;
          }
        }
        
        @keyframes rotate-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        @keyframes candle-flicker {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.6;
          }
        }
        
        @keyframes candle-flicker-delayed {
          0%, 100% {
            opacity: 0.6;
          }
          50% {
            opacity: 1;
          }
        }
        
        @keyframes pulse-delayed {
          0%, 100% {
            opacity: 0.6;
            transform: scale(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.15);
          }
        }
        
        .animate-float-slow {
          animation: float-slow 8s ease-in-out infinite;
        }
        
        .animate-float-medium {
          animation: float-medium 6s ease-in-out infinite;
        }
        
        .animate-float-fast {
          animation: float-fast 4s ease-in-out infinite;
        }
        
        .animate-spin-slow {
          animation: spin-slow 20s linear infinite;
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
        }
        
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
        
        .animate-slide-in-right {
          animation: slide-in-right 2s ease-out forwards;
        }
        
        .animate-slide-in-left {
          animation: slide-in-left 2s ease-out forwards;
        }
        
        .animate-grow {
          animation: grow 1.5s ease-out forwards;
        }
        
        .animate-draw-line {
          animation: draw-line 3s ease-out forwards;
        }
        
        .animate-draw-line-delayed {
          animation: draw-line-delayed 4s ease-out forwards;
        }
        
        .animate-rotate-slow {
          animation: rotate-slow 15s linear infinite;
        }
        
        .animate-candle-flicker {
          animation: candle-flicker 2s ease-in-out infinite;
        }
        
        .animate-candle-flicker-delayed {
          animation: candle-flicker-delayed 2s ease-in-out infinite;
        }
        
        .animate-pulse-delayed {
          animation: pulse-delayed 2.5s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default AnimatedLoginBackground;
